/**
 * Stamped rows (WB07 / NS-38): counterfeit-token and look-alike-sender rows.
 *
 * The stamps (`counterfeit`, `counterfeit_claimed_value`, `poisoning_candidate`) are set at ingest by the
 * backend (token_canon.py, address_poisoning.py). This module only READS them: it never re-derives a verdict.
 * A stamped row keeps its place in the list, carries a badge instead of reading as a 0-value transfer, and is
 * excluded from totals; the exclusion is counted so it is never silent.
 */
import { resolveUsd, isDustValue, txRowUsd } from './edgeConsolidation';

export type StampKind = 'counterfeit' | 'poisoning';

export function stampKind(tx: any): StampKind | null {
    if (tx?.counterfeit === true) return 'counterfeit';
    if (tx?.poisoning_candidate === true) return 'poisoning';
    return null;
}

function fmtAmount(n: number): string {
    return n.toLocaleString('en-US', { maximumFractionDigits: n < 1 ? 4 : 2 });
}

/** "claimed 1,000 USDT" from `counterfeit_claimed_value`, or '' when the row carries none. */
export function claimedText(tx: any): string {
    const n = Number(tx?.counterfeit_claimed_value);
    if (!Number.isFinite(n) || n <= 0) return '';
    const sym = String(tx?.counterfeit_as || tx?.token_symbol || '').trim();
    return `claimed ${fmtAmount(n)}${sym ? ' ' + sym : ''}`;
}

/** Badge text of a stamped row, '' for an ordinary row. The report counterparty table uses the same text. */
export function stampBadgeText(tx: any): string {
    const k = stampKind(tx);
    if (k === 'counterfeit') {
        const c = claimedText(tx);
        return c ? `counterfeit token (${c})` : 'counterfeit token';
    }
    if (k === 'poisoning') return 'look-alike sender';
    return '';
}

export interface StampedSummary {
    counterfeit: number;
    poisoning: number;
    /** claimed amount per token symbol, over the counterfeit rows that carry one. */
    claimed: Record<string, number>;
}

export function summarizeStamped(txs: readonly any[]): StampedSummary {
    const out: StampedSummary = { counterfeit: 0, poisoning: 0, claimed: {} };
    for (const tx of txs || []) {
        const k = stampKind(tx);
        if (k === 'counterfeit') {
            out.counterfeit += 1;
            const n = Number(tx?.counterfeit_claimed_value);
            if (Number.isFinite(n) && n > 0) {
                const sym = String(tx?.counterfeit_as || tx?.token_symbol || '').trim() || 'units';
                out.claimed[sym] = (out.claimed[sym] || 0) + n;
            }
        } else if (k === 'poisoning') {
            out.poisoning += 1;
        }
    }
    return out;
}

/** "1,000 USDT, 20 TRX" for a claimed map, '' when empty. */
export function claimedTotalText(claimed: Record<string, number>): string {
    return Object.entries(claimed).map(([sym, n]) => `${fmtAmount(n)} ${sym}`).join(', ');
}

/** Rows the dust rule hides: a KNOWN sub-cent value that is not stamped (stamped rows keep their place). */
export function isHiddenDustRow(tx: any): boolean {
    return stampKind(tx) === null && isDustValue(tx?.value, tx?.token_symbol, txRowUsd(tx));
}

/** USD of an ordinary row on the one basis, null when unknown. */
export function rowUsd(tx: any): number | null {
    return resolveUsd(tx?.value, tx?.token_symbol, txRowUsd(tx));
}

/**
 * The report's one line: "N rows hidden as dust, N counterfeit-token rows (claimed X) and N look-alike-sender
 * rows excluded from totals". From the rows only; '' when there is nothing to say.
 */
export function stampedRowsLine(txs: readonly any[]): string {
    const rows = Array.isArray(txs) ? txs : [];
    const dust = rows.reduce((n, t) => n + (isHiddenDustRow(t) ? 1 : 0), 0);
    const s = summarizeStamped(rows);
    const plural = (n: number) => (n === 1 ? 'row' : 'rows');
    const stamped: string[] = [];
    if (s.counterfeit > 0) {
        const claimed = claimedTotalText(s.claimed);
        stamped.push(`${s.counterfeit} counterfeit-token ${plural(s.counterfeit)}${claimed ? ` (claimed ${claimed})` : ''}`);
    }
    if (s.poisoning > 0) stamped.push(`${s.poisoning} look-alike-sender ${plural(s.poisoning)}`);
    const parts: string[] = [];
    if (dust > 0) parts.push(`${dust} ${plural(dust)} hidden as dust`);
    if (stamped.length > 0) {
        parts.push(`${stamped.join(' and ')} excluded from totals`);
    }
    return parts.join(', ');
}
